import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { useInView } from 'react-intersection-observer';
import {
  ILearningCenterArticle,
  ILearningCenterArticleSectionBlockFields
} from '../../../../../types/contentful';
import { ScrollReveal } from '../../../ScrollReveal';
import { ArticleSnippetList } from '../ArticleSnippetList';
import { isCanadianContext } from '../../../../lib/geolocation/linkUtils';

interface ArticlesResponse {
  items: ILearningCenterArticle[];
  isLastPage?: boolean;
}

export function ArticleSectionBlock({
  title,
  category,
  lazyLoad
}: ILearningCenterArticleSectionBlockFields) {
  const router = useRouter()?.query;
  const partnerResourcesCategory =
    useRouter().asPath.includes('partner-resources');
  const [articlesList, setArticlesList] = useState<ILearningCenterArticle[]>(
    []
  );
  const [articlesBuffer, setArticlesBuffer] = useState<
    ILearningCenterArticle[]
  >([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [lastPage, setLastPage] = useState(true);
  const [loadNextPage, setLoadNextPage] = useState(false);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const { ref, inView } = useInView();

  const savedFilters = {
    difficulties: router?.difficulties?.toString() ?? '',
    mediaTypes: router?.mediaTypes?.toString() ?? '',
    sortBy: router?.sortBy?.toString() ?? '',
    subject: router?.subject?.toString() ?? '',
    topics: router?.topics?.toString() ?? ''
  };

  useEffect(() => {
    if ((lazyLoad && !inView) || !loadNextPage || finishedLoading) {
      return;
    }
    if (lastPage && articlesBuffer.length > 0) {
      setArticlesList(articlesBuffer);
      setArticlesBuffer([]);
      setFinishedLoading(true);
      return;
    }
    fetchArticles(category ? category.fields.name : null, pageNumber, pageSize)
      .then(({ items, isLastPage }) => {
        const allItems = [...articlesBuffer, ...items];
        const itemsToAdd = allItems.slice(0, pageSize);
        const itemsToBuffer = allItems.slice(pageSize);

        setArticlesList(itemsToAdd);
        setArticlesBuffer(itemsToBuffer);

        if (isLastPage !== undefined) {
          setLastPage(isLastPage);
        }
      })
      .finally(() => setLoadNextPage(false));
  }, [
    lazyLoad,
    inView,
    loadNextPage,
    pageNumber,
    pageSize,
    savedFilters,
    articlesBuffer,
    finishedLoading
  ]);

  const fetchArticles = async (
    categoryName: string | null,
    pageNum: number,
    pageLimit: number
  ): Promise<ArticlesResponse> => {
    try {
      const isCanada = isCanadianContext();
      const countryParam = isCanada ? '&country=CA' : '&country=US';

      //Logic to decide whether to use category api or filters api
      if (
        router?.category ||
        categoryName == 'All Categories' ||
        partnerResourcesCategory
      ) {
        const url = `/api/learning-center/articles-by-category?category=${categoryName}&page=${pageNum}&limit=${pageLimit}${countryParam}`;
        const { data } = await axios.get<ArticlesResponse>(url);
        return data;
      } else {
        const url = `/api/learning-center/filtered-articles?difficulties=${savedFilters.difficulties}&mediaTypes=${savedFilters.mediaTypes}&sortBy=${savedFilters.sortBy}&subject=${savedFilters.subject}&topics=${savedFilters.topics}&page=${pageNum}&limit=${pageLimit}${countryParam}`;
        const { data } = await axios.get<ArticlesResponse>(url);
        return data;
      }
    } catch (error) {
      console.error('Error loading articles', error);
      return { items: [] } as ArticlesResponse;
    }
  };

  return (
    <div className="mb-16 desktop:mb-20">
      <ScrollReveal>
        <div
          className="block-container [&>*]:px-3 tablet:[&>*]:px-12 desktop:[&>*]:px-12"
          data-testid="article-section-block"
        >
          <div className="flex w-full flex-col" ref={ref}>
            <div className="column w-full py-4 pr-4 desktop:py-6 desktop:pr-6">
              <h2 className="text-[28px] font-medium leading-8 tablet:text-[42px] tablet:leading-12">
                {title == 'All Categories' ? '' : title}
              </h2>
            </div>
            <div className="column w-full mobile:p-0">
              <ArticleSnippetList
                articles={articlesList}
                lastPage={lastPage && articlesBuffer.length === 0}
                onLoadMore={(newPageNumber: number, newPageSize: number) => {
                  setPageNumber(newPageNumber);
                  setPageSize(newPageSize);
                  setLoadNextPage(true);
                }}
              />
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>
  );
}
